import React, { useState, useRef, useEffect, useMemo } from "react";
import classNames from "classnames";
import SVG from 'components/base/SVG';
import css from "./style.scss";

// Callers may pass either plain objects or raw Immutable.js Maps/Lists (e.g. Redux group/list selectors).
const normalizeItem = (item) => (item && typeof item.toJS === "function" ? item.toJS() : item);

const ListManagementField = ({
  lists = [],
  placeholder = "Select or Type to Create a New List",
  onSelect,
  onCreate,
  label,
  required = false,
  showLabel = true,
  createLabel = "(Create as New List)",
}) => {
  const normalizedLists = useMemo(() => Array.from(lists, normalizeItem), [lists]);

  const [query, setQuery] = useState("");
  const [filtered, setFiltered] = useState(normalizedLists);
  const [isActionDropdownOpen, setIsActionDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const [selectedItem, setSelectedItem] = useState(null);


  useEffect(() => {
    const sorted = [...normalizedLists].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })
    );
    setFiltered(sorted);
  }, [normalizedLists]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsActionDropdownOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  const handleInput = (value) => {
    setQuery(value);
    setIsActionDropdownOpen(true);

    if (!value.trim()) {
      setSelectedItem(null);
      setFiltered(normalizedLists);
      onSelect?.(null);
      return;
    }

    if (selectedItem) {
      setSelectedItem(null);
    }

    const filteredLists = normalizedLists.filter((l) =>
      (l.name || "").toLowerCase().includes(value.toLowerCase())
    );
    setFiltered(filteredLists);
  };

  const handleSelect = (item) => {
    setQuery(item.name || "");
    setSelectedItem(item);
    setIsActionDropdownOpen(false);
    onSelect?.(item);
    setFiltered(normalizedLists);
  };

  const handleCreate = () => {
    if (!query.trim()) return;
    const exists = normalizedLists.some(
      (l) => (l.name || "").toLowerCase() === query.toLowerCase()
    );
    if (!exists) {
      onCreate?.(query);
      setIsActionDropdownOpen(false);
      setQuery(query);
    }
  };

  const handleKeyDown = (e) => {
    if (!isActionDropdownOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, filtered.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex === filtered.length) handleCreate();
      else if (activeIndex >= 0) handleSelect(filtered[activeIndex]);
    }
  };

  return (
    <div className={css.wrapper} ref={wrapperRef}>
      {showLabel && (
        <label className={css.campaignLabel}>
          {label} {required && <span className={css.requiredAsterisk}> *</span>}
        </label>
      )}
      <div
        className={classNames(css.inputBox, {
          [css.open]: isActionDropdownOpen,
        })}
        onClick={() => { setIsActionDropdownOpen(true); setFiltered(normalizedLists); }}
      >
        <input
          type="text"
          value={query}
          placeholder={placeholder}
          className={css.input}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => setIsActionDropdownOpen(true)}
          onKeyDown={handleKeyDown}
        />
        <SVG
          icon="iconCaretDown"
          className={css.dropdownCaretIcon}
        />
      </div>

      {isActionDropdownOpen && (
        <ul className={css.dropdown}>
          {filtered.length > 0 ? (
            filtered.map((item, index) => (
              <li
                key={item.id}
                className={classNames(css.item, {
                  [css.active]: index === activeIndex,
                  [css.selected]: selectedItem?.id === item.id,
                })}
                onClick={() => handleSelect(item)}
              >
                {item.name}
              </li>
            ))
          ) : (
            <li className={css.noMatch}>No matches found</li>
          )}
          {query &&
            !normalizedLists.some(
              (l) => (l.name || "").toLowerCase() === query.toLowerCase()
            ) && (
              <>
                <li className={css.divider} />
                <li
                  className={classNames(css.createOption, {
                    [css.active]: activeIndex === filtered.length,
                  })}
                  onClick={handleCreate}
                >
                  {query} <span>{createLabel}</span>
                </li>
              </>
            )}
        </ul>
      )}
    </div>
  );
};

export default ListManagementField;